import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import './Layout.scss';
import Text from '../Text/Text';
import stores from '../../assets/alpha_store_logos.svg';
import useToken from '../../hooks/useToken/useToken';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import WarningModal from '../WarningModal/WarningModal';
import { MODAL_EXPIRY } from '../../utils/general/general';
import IconButton from '../IconButton/IconButton';

const Layout = () => {
  const location = useLocation();
  const { token } = useToken();
  const [warningTimer, setWarningTimer] = useState<number>(30);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);

  const handleInteraction = useCallback(() => {
    if (!showModal) {
      setWarningTimer(30);
    }
  }, [showModal]);

  const handleTimeout = useCallback(() => {
    navigate('/') as void;
    setShowModal(false);
    setWarningTimer(30);
  }, [navigate]);

  useEffect(() => {
    if (location.pathname === '/') return;

    const int = setInterval(() => {
      setWarningTimer(t => t - 1);
    }, 1000);

    document.addEventListener('mousemove', handleInteraction);
    document.addEventListener('scroll', handleInteraction);
    document.addEventListener('keydown', handleInteraction);
    document.addEventListener('click', handleInteraction);
    document.addEventListener('touchstart', handleInteraction);

    return () => {
      clearInterval(int);
      document.removeEventListener('mousemove', handleInteraction);
      document.removeEventListener('scroll', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
    };
  }, [handleInteraction, location.pathname]);

  useEffect(() => {
    if (warningTimer === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShowModal(true);
    }

    if (warningTimer === -1 * MODAL_EXPIRY) {
      handleTimeout();
    }
  }, [warningTimer, handleTimeout]);

  const showBackButton = useMemo(
    () => !(location.pathname === '/' || location.pathname === '/booking-confirmation') && token,
    [location.pathname, token],
  );

  const handleGoBack = () => {
    void navigate(-1);
  };

  return (
    <div className="layout">
      {showBackButton ? (
        <div className="back-button-container" data-testid={'back-button'}>
          <IconButton
            buttonStyleType="primary"
            handleClick={handleGoBack}
            iconName="ArrowLeft"
            size="large"
            additionalStyling={{
              borderRadius: 30,
              backgroundColor: 'var(--band-6)',
            }}
          />
        </div>
      ) : null}
      <main>
        <Outlet />
      </main>
      {showModal &&
        createPortal(
          <WarningModal
            handleTimeout={handleTimeout}
            setShowModal={setShowModal}
            warningTimer={warningTimer}
          />,
          document.body,
        )}
      {location.pathname === '/' && token ? (
        <section className="app-download">
          <Text type="callout">Download the Alpha Taxis App</Text>
          <div className="details">
            <div>
              <Text type="body">
                Enjoy the ease of card payments, real time journey updates, and live taxi tracking.
                Simply scan the QR code to download the app.
              </Text>
              <div className="stores-image">
                <img src={stores} />
              </div>
            </div>
            <div className="qr-code">qr</div>
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default Layout;
